const DEFAULT_DOB = '05/29/2023';

/** Main Functions */
export function getPageInfo() {
  /*
              Description: This function will get the page info from the note
              Return: A dictionary with the page info
                  {
                      "URL": Base URL for the page,
                      "Note Type": "PT Daily/SOAP Note",...
                  }
              */
  const baseURL = window.location.origin;

  let noteType = null;

  // Look for <b> inside a clickable <a> that appears near the top
  const candidates = document.querySelectorAll('a.flex.block b');

  for (const b of candidates) {
    const text = b.textContent?.trim();
    if (
      text &&
      !['Header', 'Subheader'].includes(text) &&
      text.length < 50 && // Avoid capturing paragraphs or content blocks
      /^[A-Za-z ]+$/.test(text) // Letters and spaces only
    ) {
      noteType = text;
      break;
    }
  }
  if (noteType === 'Footer') {
    // For ST discipline the above extraction logic was not working
    const h2 = document.querySelector(
      'h2[data-bind*="currentNote().currentSection().displayName"]'
    );
    noteType = h2 ? h2.textContent.trim() : null;
    noteType = noteType.replace(/\s*pg\s*\d+$/i, '').trim();
  }

  // Fallback: DN.htm "Create session note" input (e.g., "ST Mobile DN")
  if (!noteType || noteType === 'None' || noteType.trim() === '') {
    const primary = document.querySelector(
      '[data-testid="create-session-note"] input[aria-autocomplete="list"]'
    ) as HTMLInputElement | null;
    const primaryVal = primary?.value?.trim();
    if (primaryVal) {
      noteType = primaryVal;
    } else {
      const alt = document.querySelector(
        '[data-testid="create-session-note"] input[type="text"]'
      ) as HTMLInputElement | null;
      const altVal = alt?.value?.trim();
      if (altVal) noteType = altVal;
    }
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  let dob = '';
  let firstName = '';
  let lastName = '';
  let emrID = '';

  function extractFromDocument(doc) {
    const nameSpan = doc.querySelector('[data-content="clientFullName"]');
    if (nameSpan) {
      const rawName = nameSpan.textContent.trim();
      const nameParts = rawName.split(' ');
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
      }
    }

    const dobSpan = doc.querySelector('[data-content="clientBirthDate"]');
    if (dobSpan) {
      const rawDOB = dobSpan.textContent.trim();
      const dateObj = new Date(rawDOB);
      if (!isNaN(dateObj)) {
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const yyyy = dateObj.getFullYear();
        dob = `${mm}/${dd}/${yyyy}`;
      }
    }
  }

  // Search main document first
  extractFromDocument(document);

  // If still missing name or DOB, try all iframes
  if (!firstName || !dob) {
    const iframes = document.querySelectorAll('iframe');
    for (const iframe of iframes) {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          extractFromDocument(doc);
          if (firstName && dob) break;
        }
      } catch (e) {
        console.warn(
          '⚠️ Unable to access iframe due to cross-origin policy.',
          iframe
        );
      }
    }
  }

  // DN.htm fallback: parse name from Timesheet header and DOB from sidebar text
  if (!firstName || !lastName || !dob) {
    const header = document.querySelector(
      '#TimesheetUser h2'
    ) as HTMLElement | null;
    if (header && (!firstName || !lastName)) {
      try {
        let text = header.textContent ? header.textContent.trim() : '';
        // Remove parenthetical segments like (Client ID: 123)
        text = text.replace(/\(.*?\)/g, '').trim();
        const parts = text.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          if (!firstName) firstName = parts[0].replace(/^"|"$/g, '');
          if (!lastName)
            lastName = parts[parts.length - 1].replace(/^"|"$/g, '');
        }
      } catch {}
    }

    if (!dob) {
      try {
        const scope =
          (document.getElementById('sidebarTimesheet') as HTMLElement | null) ||
          document.body;
        const scopeText = scope?.textContent || '';
        const m = scopeText.match(
          /(?:DOB|Date of Birth)[:\s-]*([01]?\d\/[0-3]?\d\/\d{2,4})/i
        );
        if (m && m[1]) {
          dob = m[1];
        }
      } catch {}
    }
  }

  emrID = (firstName + lastName).replace(/\s+/g, '').toLowerCase();

  return {
    'EMR ID': emrID || '',
    'First Name': firstName || '',
    'Last Name': lastName || '',
    DOB: dob || '01/01/1900'
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
var questionInteractions = {};

export async function getQuestions() {
  // Must clear them out before we start
  questionInteractions = {};
  questions = [];

  try {
    await fetchFlexQuestions();
    await complexTableParsing();
    await getLongAnswerQuestions();
    await getMobileTemplatesQuestions();

    // This list must be ordered based on where the OriginalElement (DOM element) is located in the document order
    Object.keys(questionInteractions)
      .sort((a, b) => {
        const elementA = questionInteractions[a].OriginalElement;
        const elementB = questionInteractions[b].OriginalElement;
        if (
          elementA.compareDocumentPosition(elementB) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          return -1;
        } else {
          return 1;
        }
      })
      .forEach((key) => {
        if (
          key === 'Interventions Performed:' ||
          key === 'Question Table' ||
          key === 'Patient DOB:'
        )
          return;
        const question = { ...questionInteractions[key] };
        delete question.OriginalElement;
        questions.push(question);
      });

    console.log('Sorted questions:', questions);
    questions.forEach((question) => {
      console.log(question.Key);
    });

    return questions; // Return the final array of questions
  } catch (error) {
    console.error('Error in getQuestions:', error);
    return []; // Return an empty array if an error occurs
  }
}

let isModalOpen = false;

var keyCount = {};

function getLabelForElement(el) {
  // 1. Walk backward through previousElementSiblings to find a <strong>
  let prev =
    el.closest('p')?.previousElementSibling || el.previousElementSibling;
  while (prev) {
    const strong = prev.querySelector('strong');
    if (strong && strong.textContent.trim()) {
      return strong.textContent.trim();
    }
    prev = prev.previousElementSibling;
  }

  // 2. Walk up parent nodes checking their previous siblings (to go outside <p> blocks)
  let walker = el.parentElement;
  while (walker) {
    let sibling = walker.previousElementSibling;
    while (sibling) {
      const strong = sibling.querySelector('strong');
      if (strong && strong.textContent.trim()) {
        return strong.textContent.trim();
      }
      sibling = sibling.previousElementSibling;
    }
    walker = walker.parentElement;
  }

  // 3. Fallback to data-options label
  try {
    const options = JSON.parse(el.getAttribute('data-options'));
    if (options?.label?.trim()) {
      return options.label.trim();
    }
  } catch {}

  // 4. Final fallback: placeholder innerText
  const text = el.innerText?.trim();
  return text || 'Unknown';
}

function getUniqueKey(baseKey) {
  const cleanKey = baseKey || 'Unknown';
  if (!keyCount[cleanKey]) {
    keyCount[cleanKey] = 1;
    return cleanKey;
  } else {
    keyCount[cleanKey]++;
    return `${cleanKey} ${keyCount[cleanKey]}`;
  }
}

function waitForModal() {
  return new Promise((resolve) => {
    let tries = 0;
    const maxTries = 30;
    const check = setInterval(() => {
      const modal = document.querySelector('#compose-modal.modal.in');
      if (modal && modal.style.display !== 'none') {
        clearInterval(check);
        resolve(modal);
      }
      tries++;
      if (tries > maxTries) {
        clearInterval(check);
        resolve(null);
      }
    }, 300);
  });
}

function simpleMarkdownToHTML(markdown: string): string {
  const lines = markdown.split('\n');
  const htmlLines: string[] = [];
  let inList = false;

  for (let line of lines) {
    line = line.trim();

    // Handle bullet points
    if (line.startsWith('- ')) {
      if (!inList) {
        htmlLines.push('<ul>');
        inList = true;
      }

      // Convert **bold** inside bullet
      const listItem = line
        .substring(2)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

      htmlLines.push(`<li>${listItem}</li>`);
    } else {
      if (inList) {
        htmlLines.push('</ul>');
        inList = false;
      }

      // Fallback for non-list lines
      const formattedLine = line.replace(
        /\*\*(.+?)\*\*/g,
        '<strong>$1</strong>'
      );
      if (formattedLine) {
        htmlLines.push(`<p>${formattedLine}</p>`);
      }
    }
  }

  if (inList) {
    htmlLines.push('</ul>');
  }

  return htmlLines.join('\n');
}

function callToMainLandWithId(id, value) {
  value = simpleMarkdownToHTML(value);
  return new Promise((resolve, reject) => {
    const handler = (event) => {
      if (
        event.data.action === 'injectTinyMCEText_response' &&
        event.data.id === id
      ) {
        window.removeEventListener('message', handler);
        resolve(event.data.result);
      }
    };
    window.addEventListener('message', handler);

    window.postMessage(
      {
        action: 'injectTinyMCEText',
        id,
        value
      },
      '*'
    );

    setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error(`Timeout waiting for TinyMCE injection for "${id}"`));
    }, 2000);
  });
}

function clickContinueButton(modal) {
  const btn = modal?.querySelector('button.btn.btn-primary');
  if (btn) {
    btn.click();
    console.log('Clicked Continue');
  }
}

async function injectNewerTextarea(modal, newVal) {
  const textarea = modal.querySelector(
    'textarea.form-control, input.form-control'
  );
  if (!textarea) return;
  textarea.value = newVal;
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  textarea.dispatchEvent(new Event('change', { bubbles: true }));
  await new Promise((r) => setTimeout(r, 100));
  clickContinueButton(modal);
}

function processFields(doc) {
  const fields = doc.querySelectorAll(
    '[data-content="formRichTextArea"], [data-content="formTextArea"], [data-content="formTextbox"]'
  );
  console.log(`Found ${fields.length} rich/long text fields in document`);

  fields.forEach((el, index) => {
    if (el.closest('td')) return;

    const label = getLabelForElement(el);
    const key = getUniqueKey(label);
    const val = el.innerText.trim();

    questionInteractions[key] = {
      Key: key,
      Question: key,
      Type: 'Long Answer',
      ExistingNote: val,
      OriginalElement: el,
      Input: async (newVal) => {
        await injectRichTextViaModal(el, String(newVal));
      }
    };

    console.log(`Extracted field: "${key}" — Value: "${val}"`);
  });
}

async function injectRichTextViaModal(clickTarget, value) {
  if (!value || value === null) return; // Short Circuit for the case where the value is empty or null
  if (value === 'null') return; // Short Circuit for the case where the value is empty or null

  // Concurrency guard shared across flows
  while (isModalOpen) {
    await new Promise((r) => setTimeout(r, 200));
  }
  isModalOpen = true;
  try {
    clickTarget.click();
    const modal = await waitForModal();
    if (!modal) return;
    const msgId = `inject-${Date.now()}`;
    const isTinyMCE = modal.querySelector('iframe');
    if (isTinyMCE) {
      try {
        const result = await callToMainLandWithId(msgId, String(value));
        if (result === 'success') {
          await new Promise((r) => setTimeout(r, 200));
          clickContinueButton(modal);
        }
      } catch (err) {
        console.warn('TinyMCE injection error:', err);
      }
    } else {
      await injectNewerTextarea(modal, String(value));
    }
  } finally {
    isModalOpen = false;
  }
}

async function getLongAnswerQuestions() {
  keyCount = {};

  console.log('Scanning main document for form areas');
  processFields(document);

  const iframes = document.querySelectorAll('iframe');
  console.log(`Scanning ${iframes.length} iframe(s) for form areas`);
  iframes.forEach((iframe) => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) processFields(doc);
    } catch {
      console.warn('Cross-origin iframe blocked:', iframe);
    }
  });

  console.log(
    'Extraction complete. Interactions ready in `questionInteractions`'
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchFlexQuestions() {
  const documentsToCheck = [document];

  document.querySelectorAll('iframe').forEach((iframe) => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) documentsToCheck.push(doc);
    } catch (e) {
      console.warn('Cross-origin iframe skipped');
    }
  });

  documentsToCheck.forEach((doc) => {
    const tables = doc.querySelectorAll('table');

    tables.forEach((table) => {
      const rows = table.querySelectorAll('tr');
      if (rows.length < 2) return;

      // --- HARD-CODED SLP SEVERITY TABLE DETECTION ---
      // Hardcoded detection for SLP severity table
      const firstRow = rows[0]?.querySelectorAll('td');
      if (
        firstRow?.length === 2 &&
        firstRow[0].textContent
          .trim()
          .toLowerCase()
          .includes('issues identified') &&
        firstRow[1].textContent
          .trim()
          .toLowerCase()
          .includes('level of severity')
      ) {
        const sectionLabel = 'Issues Identified & Severity';
        const key = sectionLabel;
        const structure = {};
        structure[key] = {};

        Array.from(rows)
          .slice(1)
          .forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('td');
            if (cells.length !== 2) return;

            const issueCell = cells[0];
            const severityCell = cells[1];

            const issueCheckbox = issueCell.querySelector(
              '[data-content="formCheckbox"]'
            );
            const issueLabel = issueCell.textContent
              .trim()
              .split('\n')[0]
              .trim();

            const severityCheckboxes = severityCell.querySelectorAll(
              '[data-content="formCheckbox"]'
            );
            const severityLabels = Array.from(severityCheckboxes).map((cb) => {
              let labelText = '';
              let node = cb.nextSibling;
              while (
                node &&
                (node.nodeType === Node.TEXT_NODE ||
                  node.nodeType === Node.ELEMENT_NODE)
              ) {
                const text = node.textContent?.trim();
                if (text) {
                  labelText = text;
                  break;
                }
                node = node.nextSibling;
              }
              return labelText;
            });
            severityLabels.push('None');
            structure[key][issueLabel] = {
              Description: `Select severity if applicable. The available options are: ${severityLabels.join(', ')}`,
              Input: async (val) => {
                if (!val || val.toLowerCase() === 'none') return;

                // Ensure issue checkbox is selected
                if (issueCheckbox) {
                  for (let attempt = 0; attempt < 2; attempt++) {
                    if (
                      !issueCheckbox.classList.contains('checked') &&
                      !issueCheckbox.querySelector('.fa-check-square')
                    ) {
                      issueCheckbox.click();
                      await sleep(150);
                    } else {
                      break;
                    }
                  }
                }

                const valLower = val.toLowerCase().trim();

                for (let idx = 0; idx < severityCheckboxes.length; idx++) {
                  const cb = severityCheckboxes[idx];
                  const lbl = severityLabels[idx].toLowerCase().trim();
                  const isChecked =
                    cb.classList.contains('checked') ||
                    cb.querySelector('.fa-check-square');
                  const shouldCheck = lbl === valLower;

                  if (shouldCheck && !isChecked) {
                    cb.click();
                    await sleep(150);

                    // Retry if still not checked
                    const nowChecked =
                      cb.classList.contains('checked') ||
                      cb.querySelector('.fa-check-square');
                    if (!nowChecked) {
                      cb.click();
                      await sleep(100);
                    }
                    break;
                  }
                }
              }
            };
          });

        questionInteractions[key] = {
          Type: 'Flex',
          Key: key,
          Question: sectionLabel,
          Structure: structure,
          OriginalElement: table
        };
        return; // Skip further parsing of this table
      }
      // --- END HARD-CODED SLP SEVERITY TABLE DETECTION ---

      let sectionLabel = null;
      const firstRowCells = rows[0].querySelectorAll('td');
      if (
        firstRowCells.length === 1 &&
        firstRowCells[0].getAttribute('colspan')
      ) {
        sectionLabel = firstRowCells[0].textContent.trim();
      }
      if (!sectionLabel) {
        // Check if the table has a header row with 'Question' and 'Response'
        const headers = rows[0].querySelectorAll('td, th');
        if (
          headers.length === 2 &&
          headers[0].textContent.trim().toLowerCase().includes('question') &&
          headers[1].textContent.trim().toLowerCase().includes('response')
        ) {
          sectionLabel = headers[0].textContent.trim() + ' Table';
        } else {
          return;
        }
      }

      const key = sectionLabel;
      const structure = {};
      let rowCounter = 1;

      Array.from(rows)
        .slice(1)
        .forEach((row) => {
          const cells = row.querySelectorAll('td');
          if (cells.length === 0) return;

          cells.forEach((cell) => {
            const checkboxes = cell.querySelectorAll(
              '[data-content="formCheckbox"]'
            );
            const dropdown = cell.querySelector(
              '[data-content="formDropDown"]'
            );
            const textbox = cell.querySelector('[data-content="formTextbox"]');
            const richText = cell.querySelector(
              '[data-content="formRichTextArea"]'
            );
            const textArea = cell.querySelector(
              '[data-content="formTextArea"]'
            );

            if (!structure[key]) {
              structure[key] = {};
            }

            if (checkboxes.length > 0) {
              const baseLabel = cell.innerText.includes(':')
                ? cell.innerText.split(':')[0].trim()
                : cell.innerText.trim() || `Row ${rowCounter}`;

              const choices = [];

              checkboxes.forEach((cb) => {
                let labelText = '';
                let node = cb.nextSibling;

                // Collect text immediately following checkbox
                while (
                  node &&
                  (node.nodeType === Node.TEXT_NODE ||
                    node.nodeType === Node.ELEMENT_NODE)
                ) {
                  if (node.textContent.trim()) {
                    labelText += node.textContent.trim() + ' ';
                  }
                  node = node.nextSibling;
                  if (labelText.length > 1) break;
                }

                labelText = labelText.trim();
                if (labelText) choices.push(labelText);
              });

              const waitForCheckboxesAndApply = async (checkboxes, val) => {
                // Wait until all checkboxes are visible and attached
                for (let i = 0; i < 20; i++) {
                  // try up to 2 seconds
                  const allVisible = Array.from(checkboxes).every((cb) =>
                    document.body.contains(cb)
                  );
                  if (allVisible) break;
                  await new Promise((r) => setTimeout(r, 100));
                }
                checkboxes.forEach((cb) => {
                  let labelText = '';
                  let node = cb.nextSibling;
                  while (
                    node &&
                    (node.nodeType === Node.TEXT_NODE ||
                      node.nodeType === Node.ELEMENT_NODE)
                  ) {
                    const text = node.textContent?.trim();
                    if (text) {
                      labelText = text;
                      break;
                    }
                    node = node.nextSibling;
                  }
                  const shouldCheck = Array.isArray(val)
                    ? val.some(
                        (userVal) =>
                          userVal.toLowerCase().trim() ===
                          labelText.toLowerCase().trim()
                      )
                    : val.toLowerCase().trim() ===
                      labelText.toLowerCase().trim();
                  const isChecked =
                    cb.classList.contains('checked') ||
                    cb.querySelector('.fa-check-square');

                  if (shouldCheck && !isChecked) {
                    cb.click();
                  } else if (!shouldCheck && isChecked) {
                    cb.click();
                  }
                });
              };
              const inputFunc = (val) =>
                waitForCheckboxesAndApply(checkboxes, val);

              structure[key][baseLabel] = {
                Description: `Select all applicable answers. If none apply, reply with ‘None’. Provide your response as a list of choices separated by a ‘$’ symbol. The available options are: ${choices.join(', ')}`,
                Input: inputFunc
              };
              rowCounter++;
            }

            if (dropdown) {
              const rowEl = cell.closest('tr');
              const labelCell = rowEl?.querySelector('td');
              const label =
                labelCell?.textContent?.trim() || `Dropdown ${rowCounter}`;

              let choices = [];
              const opts = dropdown.getAttribute('data-options');
              if (opts) {
                try {
                  const parsed = JSON.parse(opts);
                  choices =
                    parsed?.questionOptions?.map((opt) =>
                      opt.text.trim().replace(/"/g, "'")
                    ) || [];
                } catch {}
              }

              const inputFunc = (val) => {
                dropdown.click();
                const match = choices.find(
                  (c) =>
                    c.toLowerCase() === val.toLowerCase() ||
                    c.toLowerCase() === val.toLowerCase().replace(/ /g, '_')
                );
                if (match) {
                  dropdown.innerText = match;
                  dropdown.dispatchEvent(new Event('input', { bubbles: true }));
                }
              };

              structure[key][label] = {
                Description: `Select the appropriate answer from the dropdown. If none apply, reply with ‘None’. The available options are: ${choices.join(', ')}`,
                Input: inputFunc
              };
              rowCounter++;
            }

            if (textbox) {
              const baseLabel =
                textbox.getAttribute('data-options')?.label ||
                `Textbox ${rowCounter}`;
              const inputFunc = (val) => {
                textbox.innerText = val;
                textbox.dispatchEvent(new Event('input', { bubbles: true }));
              };

              let uniqueLabel = baseLabel;
              let counter = 2;
              while (structure[key][uniqueLabel]) {
                uniqueLabel = `${baseLabel} (${counter})`;
                counter++;
              }

              structure[key][uniqueLabel] = {
                Input: inputFunc
              };
              rowCounter++;
            }

            if (richText) {
              const rowEl = cell.closest('tr');
              const labelCell = rowEl?.querySelector('td');
              const baseLabel =
                labelCell?.textContent?.trim() || `Rich Text ${rowCounter}`;
              const inputFunc = async (val) =>
                injectRichTextViaModal(richText as HTMLElement, String(val));

              let uniqueLabel = baseLabel;
              let counter = 2;
              while (structure[key][uniqueLabel]) {
                uniqueLabel = `${baseLabel} (${counter})`;
                counter++;
              }

              structure[key][uniqueLabel] = {
                Input: inputFunc
              };
              rowCounter++;
            }
            if (textArea) {
              const rowEl = cell.closest('tr');
              const labelCell = rowEl?.querySelector('td');
              const baseLabel =
                labelCell?.textContent?.trim() || `Rich Text ${rowCounter}`;
              const inputFunc = (val) => {
                // These also need to use the pop-up navigation
                injectRichTextViaModal(textArea as HTMLElement, String(val));
              };

              let uniqueLabel = baseLabel;
              let counter = 2;
              while (structure[key][uniqueLabel]) {
                uniqueLabel = `${baseLabel} (${counter})`;
                counter++;
              }

              structure[key][uniqueLabel] = {
                Input: inputFunc
              };
              rowCounter++;
            }
          });
        });

      questionInteractions[key] = {
        Type: 'Flex',
        Key: key,
        Question: sectionLabel,
        Structure: structure,
        OriginalElement: table
      };
    });
  });

  console.log('Structured tables parsed with Flex:', questionInteractions);
}

async function complexTableParsing() {
  const documentsToCheck = [document];

  document.querySelectorAll('iframe').forEach((iframe) => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) documentsToCheck.push(doc);
    } catch {
      console.warn('Cross-origin iframe skipped');
    }
  });

  function getCheckboxLabel(cb) {
    let labelText = '';
    let node = cb.nextSibling;
    while (node) {
      if (node.textContent?.trim()) {
        labelText = node.textContent.trim();
        break;
      }
      node = node.nextSibling;
    }
    return labelText || cb.parentElement?.textContent.trim() || 'Unknown';
  }

  function getDropdownChoices(dd) {
    try {
      return JSON.parse(dd.getAttribute('data-options')).questionOptions.map(
        (o) => o.text.trim()
      );
    } catch {
      return [];
    }
  }

  async function openAndFillModal(triggerEl, value, isRichText) {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    let selectEl = null;
    let textEl = null;
    let modal = null;

    if (!isRichText) {
      if (!triggerEl) {
        console.error('No trigger element to click');
        return false;
      }

      // Step 1: Click trigger to open modal
      ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(
        (evt) => {
          triggerEl.dispatchEvent(
            new MouseEvent(evt, {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
        }
      );

      modal = await waitForModal();
      if (!modal) {
        console.error('Modal did not appear');
        return false;
      }
      console.log('Modal opened');

      // Step 3: Detect input type
      selectEl = modal.querySelector('select.form-control');
      textEl = modal.querySelector('textarea');
    }

    if (selectEl) {
      // Dropdown flow
      const options = Array.from(selectEl.options);
      let match = options.find(
        (o) => o.textContent.trim().toLowerCase() === value.trim().toLowerCase()
      );
      if (!match)
        match = options.find((o) =>
          o.textContent.toLowerCase().includes(value.toLowerCase())
        );
      if (!match) {
        console.error('Option not found:', value);
        console.log(
          'Available:',
          options.map((o) => o.textContent.trim())
        );
        return false;
      }
      selectEl.value = match.value;
      selectEl.dispatchEvent(new Event('input', { bubbles: true }));
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('Selected:', match.textContent);
    } else if (isRichText || textEl) {
      // TinyMCE / textarea flow
      await injectRichTextViaModal(triggerEl, String(value));
      console.log('TinyMCE text inserted');
      for (let i = 0; i < 15; i++) {
        // max ~3s wait
        const activeModal = Array.from(
          document.querySelectorAll('#compose-modal .modal-content')
        ).find((m) => getComputedStyle(m.parentElement).display !== 'none');
        if (!activeModal) {
          console.log('Modal closed');
          return true;
        }
        await sleep(200);
      }
      return true;
    } else {
      console.error('No usable input (select/textarea/TinyMCE) found in modal');
      return false;
    }

    await sleep(100);

    // Step 4: Click Continue if available
    if (modal) {
      clickContinueButton(modal);
      await sleep(100);

      // Step 5: Wait for modal to close
      for (let i = 0; i < 15; i++) {
        // 🔽 3s max wait
        const activeModal = Array.from(
          document.querySelectorAll('#compose-modal .modal-content')
        ).find((m) => getComputedStyle(m.parentElement).display !== 'none');
        if (!activeModal) {
          console.log('Modal closed');
          return true;
        }
        await sleep(200);
      }
      console.warn('Modal did not close in time');
    }

    return true; // allow execution to continue
  }

  for (const doc of documentsToCheck) {
    const tables = doc.querySelectorAll('table');
    tables.forEach((table) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      if (!rows.length) return;
      const tableText = table.textContent.toLowerCase();

      const structure = {};
      let sectionLabel = 'Untitled Section';
      let prev = table.previousElementSibling;
      while (prev) {
        const strong = prev.querySelector('strong');
        if (strong?.textContent.trim()) {
          sectionLabel = strong.textContent.trim();
          break;
        }
        prev = prev.previousElementSibling;
      }

      const contentRows = rows.slice(1); // skip header row
      const allHaveDropDown =
        contentRows.length > 0 &&
        contentRows.some(
          (row) =>
            row.querySelector('[data-content="formDropDown"]') ||
            row.querySelector('[data-content="formTextbox"]')
        ) &&
        contentRows.some((row) =>
          row.querySelector('[data-content="formRichTextArea"]')
        );

      if (allHaveDropDown) {
        const struct = {};
        contentRows.forEach((row, idx) => {
          const dd = row.querySelector('[data-content="formDropDown"]');
          const tb = row.querySelector('[data-content="formTextbox"]');
          const rt = row.querySelector('[data-content="formRichTextArea"]');
          const choices = dd ? getDropdownChoices(dd) : [];

          struct[idx] = {
            'Activity/CPT Code': {
              Choices: choices,
              Input: async (val) => {
                if (
                  dd?.disabled ||
                  dd?.value === val ||
                  tb?.disabled ||
                  tb?.value === val ||
                  val === '-1' ||
                  val === '' ||
                  val === 'None' ||
                  !val
                )
                  return;
                await openAndFillModal(dd || tb, val, tb ? true : false);
              }
            },
            Comment: {
              'Existing Value': rt ? rt.innerText.trim() || '' : '',
              Input: async (val) => {
                if (
                  rt.disabled ||
                  rt.value === val ||
                  val === '-1' ||
                  val === '' ||
                  val === 'None' ||
                  !val
                )
                  return;
                await openAndFillModal(rt, val, true);
              }
            }
          };
        });
        questionInteractions[sectionLabel] = {
          Type: 'Flex',
          Key: sectionLabel,
          Question: sectionLabel,
          Structure: struct,
          OriginalElement: table
        };
        return; // skip further parsing for this table
      }
      const firstRow = rows[0].querySelectorAll('td');
      const secondRow = rows[1]?.querySelectorAll('td');

      const containsAquaticLabels =
        /streamlining|turbulence|breaking surface tension/.test(tableText);
      const firstTwoRowsCheckbox =
        Array.from(rows[0].querySelectorAll('[data-content="formCheckbox"]'))
          .length > 0 &&
        Array.from(rows[1]?.querySelectorAll('[data-content="formCheckbox"]'))
          .length > 0;

      // Aquatic table
      if (containsAquaticLabels && firstTwoRowsCheckbox) {
        structure[sectionLabel] = {};
        const label1 = rows[0].textContent.split(':')[0].trim();
        const label2 = rows[1].textContent.split(':')[0].trim();

        [rows[0], rows[1]].forEach((row, idx) => {
          const checkboxes = Array.from(
            row.querySelectorAll('[data-content="formCheckbox"]')
          );
          const label = idx === 0 ? label1 : label2;
          const choices = checkboxes.map(getCheckboxLabel);

          structure[sectionLabel][label] = {
            Description: `Select all applicable answers. If none apply, reply with ‘None’. Provide your response as a list of choices separated by a ‘$’ symbol. The available options are: ${choices.join(', ')}`,
            Input: (val) => {
              checkboxes.forEach((cb) => {
                const label = getCheckboxLabel(cb);
                const isChecked =
                  cb.classList.contains('checked') ||
                  cb.querySelector('.fa-check-square');
                const shouldCheck = Array.isArray(val)
                  ? val.some((v) => v.toLowerCase() === label.toLowerCase())
                  : val.toLowerCase() === label.toLowerCase();
                if (shouldCheck !== isChecked) cb.click();
              });
            }
          };
        });

        Array.from(rows)
          .slice(2)
          .forEach((row) => {
            const cells = row.querySelectorAll('td');
            if (cells.length !== 2) return;
            const label = cells[0].textContent.trim() + ' - Notes';
            const rich = cells[1].querySelector(
              '[data-content="formRichTextArea"]'
            );
            const plain = cells[1].querySelector(
              '[data-content="formTextArea"]'
            );

            if (rich) {
              structure[sectionLabel][label] = {
                Input: async (val) => injectRichTextViaModal(rich, String(val))
              };
            } else if (plain) {
              structure[sectionLabel][label] = {
                Input: (val) => {
                  injectRichTextViaModal(plain, String(val));
                }
              };
            }
          });
      }

      // Billing-like: checkbox grid
      else if (
        Array.from(rows).every((row) =>
          Array.from(row.querySelectorAll('td')).every((cell) =>
            cell.querySelector('[data-content="formCheckbox"]')
          )
        )
      ) {
        const billingChoices = [];
        const checkboxMap = {};

        rows.forEach((row) => {
          row.querySelectorAll('td').forEach((cell) => {
            const cb = cell.querySelector('[data-content="formCheckbox"]');
            if (!cb) return;
            const label = getCheckboxLabel(cb);
            billingChoices.push(label);
            checkboxMap[label] = cb;
          });
        });
        questionInteractions[sectionLabel] = {
          Question: sectionLabel,
          Key: sectionLabel,
          Type: 'Choose All',
          Choices: billingChoices,
          OriginalElement: table,
          Input: async (val) => {
            for (const [label, cb] of Object.entries(checkboxMap)) {
              const shouldCheck = val.includes(label);
              const isChecked =
                cb.classList.contains('checked') ||
                cb.querySelector('.fa-check-square');

              if (shouldCheck !== isChecked) {
                cb.click();
                cb.dispatchEvent(new Event('input', { bubbles: true }));
                cb.dispatchEvent(new Event('change', { bubbles: true }));
                await sleep(150);

                // Retry once if not correct
                const nowChecked =
                  cb.classList.contains('checked') ||
                  cb.querySelector('.fa-check-square');

                if (shouldCheck !== nowChecked) {
                  cb.click();
                  await sleep(100);
                }
              }
            }
          }
        };
      }

      // Generic flex table
      else {
        structure[sectionLabel] = {};
        rows.forEach((row) => {
          const cells = row.querySelectorAll('td');
          if (cells.length !== 2) return;
          const label = cells[0].textContent.trim();
          const answerCell = cells[1];

          const cb = answerCell.querySelector('[data-content="formCheckbox"]');
          const dd = answerCell.querySelector('[data-content="formDropDown"]');
          const tb = answerCell.querySelector('[data-content="formTextbox"]');
          const rt = answerCell.querySelector(
            '[data-content="formRichTextArea"]'
          );

          if (cb) {
            structure[sectionLabel][label] = {
              Input: () => cb.click()
            };
          } else if (dd) {
            let choices = [];
            try {
              choices = JSON.parse(
                dd.getAttribute('data-options')
              ).questionOptions.map((o) => o.text.trim());
            } catch {}
            structure[sectionLabel][label] = {
              Description: `Select all applicable answers. If none apply, reply with ‘None’. Provide your response as a list of choices separated by a ‘$’ symbol. The available options are: ${choices.join(', ')}`,
              Input: (val) => {
                dd.click();
                dd.innerText = val;
                dd.dispatchEvent(new Event('input', { bubbles: true }));
              }
            };
          } else if (tb) {
            structure[sectionLabel][label] = {
              Input: (val) => {
                injectRichTextViaModal(tb, String(val));
              }
            };
          } else if (rt) {
            structure[sectionLabel][label] = {
              Input: async (val) => injectRichTextViaModal(rt, String(val))
            };
          }
        });
      }

      // Filter out empty or dummy structures
      const innerKeys = Object.keys(structure[sectionLabel] || {});
      const isEmpty = innerKeys.every((k) => {
        const entry = structure[sectionLabel][k];
        return (
          !entry ||
          (typeof entry === 'object' && Object.keys(entry).length === 0)
        );
      });
      if (!isEmpty) {
        questionInteractions[sectionLabel] = {
          Type: 'Flex',
          Key: sectionLabel,
          Question: sectionLabel,
          Structure: structure,
          OriginalElement: table
        };
      }
    });
  }

  console.log('✅ All Flex Tables:', questionInteractions);
}

function getUniqueKeyForMobileTemplates(base, seenSet, usedKeys, sectionLabel) {
  if (!base || base.toLowerCase().startsWith('untitled')) return null;

  // If this section is NOT "Objective Session Information:", don’t increment, just return base once
  if (sectionLabel !== 'Objective Session Information:') {
    if (seenSet.has(base)) return null; // skip duplicates silently
    seenSet.add(base);
    return base;
  }

  // Increment logic ONLY for Objective Session Information
  if (seenSet.has(base) && !usedKeys[base]) return null;
  seenSet.add(base);

  if (!usedKeys[base]) {
    usedKeys[base] = 1;
    return base;
  } else {
    usedKeys[base]++;
    return `${base} (${usedKeys[base]})`;
  }
}

function buildObjectiveSessionInfo(sectionEl) {
  const structure = {};
  let counter = 1;

  const headers = sectionEl.querySelectorAll('p');
  headers.forEach((p) => {
    if (!p.innerText.trim().startsWith('Interventions Performed')) return;

    const container = p.closest('fieldset, div');
    if (!container) return;

    const groupName = `Interventions Performed (${counter})`;
    structure[groupName] = {};

    const checkboxes = container.querySelectorAll("input[type='checkbox']");
    const radios = container.querySelectorAll("input[type='radio']");
    let options = [];

    if (checkboxes.length > 0) {
      options = Array.from(checkboxes).map((cb) =>
        cb.closest('label')?.innerText.trim()
      );
      structure[groupName]['Choose All'] = {
        Description: `Select all applicable answers. Options: ${options.join(', ')}`,
        Input: (vals) => {
          vals = Array.isArray(vals)
            ? vals.map((v) => v.toLowerCase())
            : [vals.toLowerCase()];
          checkboxes.forEach((cb) => {
            const lbl = cb.closest('label')?.innerText.trim().toLowerCase();
            const shouldCheck = vals.includes(lbl);
            if (cb.checked !== shouldCheck) cb.click();
          });
        }
      };
    } else if (radios.length > 0) {
      options = Array.from(radios).map((rb) =>
        rb.closest('label')?.innerText.trim()
      );
      structure[groupName]['Choose One'] = {
        Choices: options,
        Input: (vals) => {
          vals = Array.isArray(vals)
            ? vals.map((v) => v.toLowerCase())
            : [vals.toLowerCase()];
          radios.forEach((rb) => {
            const lbl = rb.closest('label')?.innerText.trim().toLowerCase();
            const shouldCheck = vals.includes(lbl);
            if (rb.checked !== shouldCheck) rb.click();
          });
        }
      };
    }

    // Adjust start node: for radios, start from container; for checkboxes, start from container.parentElement
    let siblingStart =
      checkboxes.length > 0 ? container.parentElement : container;
    let sibling = siblingStart.nextElementSibling;

    while (sibling) {
      const label = sibling.querySelector('label');
      if (
        label?.innerText.trim().startsWith('Interventions Performed') ||
        label?.innerText.trim().startsWith('Optional Information')
      ) {
        break; // stop at next block
      }
      if (label?.innerText.trim().startsWith('Notes')) {
        const textField = sibling.querySelector("textarea, input[type='text']");
        if (textField) {
          structure[groupName]['Notes'] = {
            Input: (val) => {
              textField.value = val;
              textField.dispatchEvent(new Event('input', { bubbles: true }));
              textField.dispatchEvent(new Event('change', { bubbles: true }));
            }
          };
        }
        break;
      }
      sibling = sibling.nextElementSibling;
    }

    counter++;
  });

  return structure;
}

function buildStructureForSection(sectionEl, sectionLabel) {
  const structure = {};
  if (!structure[sectionLabel]) {
    structure[sectionLabel] = {}; // top-level section key
  }

  const seenLabels = new Set();
  const usedKeys = {}; // scoped to this section

  const fieldGroups = sectionEl.querySelectorAll(
    "fieldset, [role='radiogroup'], div"
  );

  fieldGroups.forEach((group) => {
    let groupLabel =
      group.querySelector('p,label')?.innerText.trim() ||
      group.previousElementSibling?.innerText?.trim() ||
      '';

    const checkboxes = group.querySelectorAll('input[type="checkbox"]');
    const radios = group.querySelectorAll('input[type="radio"]');
    const textarea = group.querySelector('textarea');

    // collect options
    let options = [];
    if (checkboxes.length > 0) {
      options = Array.from(checkboxes).map(
        (cb) => cb.closest('label')?.innerText.trim() || cb.value || 'Option'
      );
    }
    if (radios.length > 0) {
      options = Array.from(radios).map(
        (rb) => rb.closest('label')?.innerText.trim() || rb.value || 'Option'
      );
    }

    // skip if label is literally one of the options
    if (
      groupLabel &&
      options.some((opt) => opt.toLowerCase() === groupLabel.toLowerCase())
    ) {
      return;
    }

    if (
      groupLabel.includes('Notes') ||
      groupLabel.includes('Interventions Performed')
    ) {
      return;
    }
    // assign unique key
    groupLabel = getUniqueKeyForMobileTemplates(
      groupLabel,
      seenLabels,
      usedKeys,
      sectionLabel
    );
    if (!groupLabel) return;

    if (checkboxes.length > 0) {
      structure[sectionLabel][groupLabel] = {
        Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. Options: ${options.join(', ')}`,
        Input: (val) => {
          // normalize values into lowercase array
          const values = Array.isArray(val)
            ? val.map((v) => v.toLowerCase())
            : [val.toLowerCase()];

          let delay = 0;
          const step = 200; // ms between clicks

          checkboxes.forEach((cb) => {
            const labelEl = cb.closest('label');
            const lbl = labelEl?.innerText.trim().toLowerCase();

            // "none" clears all
            const shouldCheck = values.includes('none')
              ? false
              : values.includes(lbl);

            if (cb.checked !== shouldCheck) {
              setTimeout(() => {
                cb.dispatchEvent(new PointerEvent('click', { bubbles: true }));
              }, delay);
              delay += step;
            }
          });
        }
      };
    }

    if (radios.length > 0) {
      const cleanOptions = options.map(
        (opt) => opt.replace(/\\"/g, '"').replace(/"/g, '') // strip escaped and normal quotes
      );
      structure[sectionLabel][groupLabel] = {
        Choices: cleanOptions,
        Input: (val) => {
          radios.forEach((rb) => {
            const lbl =
              rb.closest('label')?.innerText.trim().toLowerCase() ||
              rb.value.toLowerCase();
            if (lbl === val.toLowerCase() && !rb.checked) rb.click();
          });
        }
      };
    }

    if (textarea) {
      structure[sectionLabel][groupLabel] = {
        Input: (val) => {
          textarea.value = val;
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
          textarea.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
    }
  });

  return structure;
}

async function getMobileTemplatesQuestions() {
  const sections = document.querySelectorAll('div:has(header h6)');

  const SKIP_SECTIONS = [
    'company header',
    'learner information',
    'provider information',
    'appointment information',
    'objective: goal review'
  ];

  sections.forEach((section) => {
    const header = section.querySelector('h6');
    if (!header) return;
    let sectionLabel = header.innerText.trim();

    if (SKIP_SECTIONS.some((s) => sectionLabel.toLowerCase().startsWith(s)))
      return;

    let structure = {};

    if (sectionLabel === 'Objective Session Information:') {
      // build both structures
      let objectiveStruct = {};

      objectiveStruct = buildObjectiveSessionInfo(section); // flat

      const genericWrapper =
        buildStructureForSection(section, sectionLabel) || {};
      const genericStruct = genericWrapper[sectionLabel] || {};

      // merge Optional Information (and any other extras)
      Object.keys(genericStruct).forEach((key) => {
        if (!objectiveStruct[key]) {
          objectiveStruct[key] = genericStruct[key];
        }
      });

      structure = objectiveStruct; // flat object
    } else {
      structure = buildStructureForSection(section, sectionLabel);
    }

    // check keys on structure directly
    if (Object.keys(structure).length > 0) {
      questionInteractions[sectionLabel] = {
        Question: sectionLabel,
        Key: sectionLabel,
        Type: 'Flex',
        Structure: structure, // flat or unwrapped
        OriginalElement: section
      };
    }
  });

  console.log('Parsed Mobile Templates Questions:', questionInteractions);
}

export async function answerQuestions(answers) {
  const errors = [];

  for (const answer of answers) {
    const error = await answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  }

  console.error('Errors:', errors);
  return errors;
}

var isLocked = false;
export async function answerQuestion(
  question,
  answer,
  replyType,
  totalWaitTime = 0
) {
  if (!questionInteractions[question]) {
    console.error(`Question not found: "${question}"`);
    return 'Question not found';
  }
  // function answerQuestion(question, answer) {
  /*
              Description: This function will take the answer and populate the question
              
              Args: 
                  question: The question in question
                  answer: The answer to the question
             
              Return: null if success otherwise a string with the error message
              */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }
  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.log(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // if we are locked, delay for a random amount of time between .1 and 1 seconds waiting no longer than 3 seconds
  if (isLocked && totalWaitTime < 3000) {
    let delay = Math.random() * 1000;
    return sleep(delay).then(() => {
      return answerQuestion(question, answer, replyType, totalWaitTime + delay);
    });
  }

  // Se our lock
  isLocked = true;

  try {
    // If it is a short answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      await questionInteractions[question]['Input'](answer, replyType);
      isLocked = false;
      return true;
    }

    // Check if the answer is a list of answers or just a single answer
    if (Array.isArray(answer)) {
      // Choose All
      var notFoundAnswers = [];
      for (var i = 0; i < answer.length; i++) {
        if (answer[i] in questionInteractions[question]['Choices']) {
          console.log('Answer found ' + answer[i]);
          await questionInteractions[question]['Choices'][answer[i]](true);
          // sleep(1000).then(() => {
          //     questionInteractions[question][answer[i]]()
          // });
        } else {
          notFoundAnswers.push(answer[i]);
        }
      }
      if (notFoundAnswers.length > 0) {
        console.error(
          'The following answers were not found: ' + notFoundAnswers
        );
        return 'The following answers were not found: ' + notFoundAnswers;
      } else {
        return true;
      }
    } else {
      // Multiple Choice
      if (answer in questionInteractions[question]['Choices']) {
        await questionInteractions[question]['Choices'][answer]();
        return true;
      } else {
        console.error('Answer not found');
        // console.log('Question: ' + question);
        // console.log('Answer:' + answer);
        // console.log(questionInteractions[question]['Choices']);
        return 'Answer not found';
      }
    }
  } finally {
    isLocked = false;
  }
}

async function answerFlexQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  try {
    // If we are locked, delay for a random amount of time between 0.1 and 1 seconds, waiting no longer than 3 seconds
    if (isLocked && totalWaitTime < 3000) {
      let delay = Math.random() * 1000;
      return sleep(delay).then(() => {
        return answerFlexQuestion(
          question,
          answer,
          replyType,
          totalWaitTime + delay
        );
      });
    }

    // Set the lock
    isLocked = true;

    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    const structure = questionObject.Structure || {};

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
        // Handle $-separated values for checkbox inputs
        const valuesToInput =
          typeof value === 'string'
            ? value.split('$').map((v) => v.trim())
            : [value];

        for (const val of valuesToInput) {
          if (field[subFieldKey]?.Input) {
            try {
              if (/(Functional Goal).*(PLOF|CLOF|Goal)/.test(subFieldKey)) {
                await sleep(200);
              }
              await field[subFieldKey].Input(val, replyType);
            } catch (error) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${error.message}`
              });
            }
          } else {
            console.warn(
              `Input function not found for "${fieldKey}.${subFieldKey}"`
            );
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
          }
        }
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  } finally {
    // Release the lock
    isLocked = false;
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
